(() => {
  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const OPENING_EXIT_START = 0.04;
  const OPENING_EXIT_END = 0.38;
  const OPENING_EXIT_DISTANCE = -112;
  const TRANSITION_REVEAL_START = 0.06;
  const TRANSITION_REVEAL_END = 0.42;

  const parseJson = (root, selector, fallback) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || "");
    } catch {
      return fallback;
    }
  };

  class NamiAmbientVideo {
    constructor(container, signal) {
      const video = container.querySelector('video');
      if (!video) return;
      let pending = false;
      let frame = null;
      const visible = () => {
        const bounds = container.getBoundingClientRect();
        return document.visibilityState !== 'hidden' && bounds.bottom > 0 &&
          bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth;
      };
      const listen = (target, event, handler, options = {}) => {
        target.addEventListener(event, handler, { ...options, signal });
      };
      const hide = () => {
        container.removeAttribute('data-video-playing');
        if (frame !== null) video.cancelVideoFrameCallback?.(frame);
        frame = null;
      };
      const reveal = () => {
        frame = null;
        if (!signal.aborted && !video.paused && video.readyState >= 2 && visible()) {
          container.setAttribute('data-video-playing', '');
        }
      };
      const decoded = () => {
        if (signal.aborted || video.paused || frame !== null) return;
        if (video.requestVideoFrameCallback) frame = video.requestVideoFrameCallback(reveal);
        else if (video.currentTime > 0) reveal();
      };
      const play = () => {
        if (signal.aborted || !visible() || pending) return;
        if (!video.paused && !video.ended) { decoded(); return; }
        video.muted = true;
        video.defaultMuted = true;
        if (video.ended) video.currentTime = 0;
        pending = true;
        try {
          Promise.resolve(video.play()).then(decoded, hide).finally(() => { pending = false; });
        } catch {
          pending = false;
          hide();
        }
      };
      const syncVisibility = () => {
        if (visible()) play();
        else { hide(); video.pause(); }
      };
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = false;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.removeAttribute('controls');
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      ['playing', 'timeupdate'].forEach(event => listen(video, event, decoded));
      ['pause', 'emptied', 'error'].forEach(event => listen(video, event, hide));
      ['loadeddata', 'canplay', 'ended'].forEach(event => listen(video, event, play));
      ['touchend', 'pointerup', 'keydown'].forEach(event => listen(document, event, play, { passive: true }));
      listen(document, 'visibilitychange', syncVisibility);
      listen(window, 'pageshow', syncVisibility);
      listen(window, 'resize', syncVisibility);
      let observer;
      if (typeof IntersectionObserver !== 'undefined') {
        observer = new IntersectionObserver(syncVisibility, { threshold: 0 });
        observer.observe(container);
      } else {
        listen(window, 'scroll', syncVisibility, { passive: true });
      }
      signal.addEventListener('abort', () => {
        hide();
        observer?.disconnect();
        video.pause();
      }, { once: true });
      syncVisibility();
    }
  }

  class NamiProductExperience {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.variants = parseJson(root, "[data-nami-variants]", []);
      this.config = parseJson(root, "[data-nami-config]", {});
      this.frameUrls = parseJson(root, "[data-nami-frames]", []);
      this.controls = [...root.querySelectorAll("[data-nami-controls]")];
      this.state = { gender: null, size: null, adding: false };
      this.frameBitmaps = new Array(this.frameUrls.length).fill(null);
      this.lastDrawnFrame = -1;
      this.storyRaf = null;
      this.layoutRaf = null;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      this.bindLayout();
      this.bindHeroVideo();
      this.bindGallery();
      this.bindProductControls();
      this.bindSizeGuide();
      this.bindTabs();
      this.bindAccordions();
      this.bindNewsletter();
      this.bindStory();
      this.updateLayout();
    }

    listen(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.signal });
    }

    bindHeroVideo() {
      this.root.querySelectorAll('[data-ambient-video]').forEach(container => {
        new NamiAmbientVideo(container, this.signal);
      });
    }

    bindLayout() {
      this.header = this.root.querySelector("[data-nami-header]");
      this.openingPurchase = this.root.querySelector("[data-opening-purchase]");
      this.hero = this.root.querySelector(".hero");
      this.transition = this.root.querySelector("[data-story-transition]");
      this.realPdp = this.root.querySelector("[data-real-pdp]");

      const requestLayout = () => {
        if (this.layoutRaf !== null) return;
        this.layoutRaf = window.requestAnimationFrame(() => this.updateLayout());
      };

      this.listen(window, "scroll", requestLayout, { passive: true });
      this.listen(window, "resize", requestLayout);
    }

    updateLayout() {
      this.layoutRaf = null;
      const viewportHeight = Math.max(window.innerHeight, 1);
      const isMobile = window.innerWidth < 1024;
      const artboardScale = Math.min(window.innerWidth / 1440, viewportHeight / 940);
      const finalPdpBounds = this.realPdp?.getBoundingClientRect();
      const mobilePdpAtHeader = Boolean(
        isMobile &&
        finalPdpBounds &&
        finalPdpBounds.top <= 66 &&
        finalPdpBounds.bottom > 64
      );

      this.root.querySelectorAll("[data-nami-artboard]").forEach((artboard) => {
        artboard.style.setProperty("--artboard-scale", String(artboardScale));
      });

      if (!isMobile && this.hero) {
        const artboardLeft = (window.innerWidth - 1440 * artboardScale) / 2;
        const artboardTop = (viewportHeight - 940 * artboardScale) / 2;
        Object.assign(this.hero.style, {
          top: `${(0 - artboardTop) / artboardScale}px`,
          bottom: "auto",
          left: `${(0 - artboardLeft) / artboardScale}px`,
          width: `${window.innerWidth / artboardScale}px`,
          height: `${viewportHeight / artboardScale}px`,
        });
      } else if (this.hero) {
        ["top", "bottom", "left", "width", "height"].forEach((property) => {
          this.hero.style.removeProperty(property);
        });
      }

      if (!isMobile && this.hero && this.transition) {
        const heroBounds = this.hero.getBoundingClientRect();
        const transitionBounds = this.transition.getBoundingClientRect();
        const transitionWidth = Math.max(transitionBounds.width, 1);
        const insetLeft = clamp(
          ((heroBounds.left - transitionBounds.left) / transitionWidth) * 100,
          0,
          50
        );
        const insetRight = clamp(
          ((heroBounds.right - transitionBounds.left) / transitionWidth) * 100,
          50,
          100
        );
        this.transition.style.setProperty("--transition-inset-left", `${insetLeft}%`);
        this.transition.style.setProperty("--transition-inset-right", `${insetRight}%`);
      }

      const headerFrame = this.header?.querySelector(".header");
      if (headerFrame && isMobile) {
        Object.assign(headerFrame.style, { left: "16px", width: "calc(100% - 32px)", top: "0px", height: "64px" });
      } else if (headerFrame) {
        const headerInset = window.innerWidth < 768 ? 12 : 32;
        const headerHeight = Math.min(78, Math.max(58, viewportHeight * 0.072));
        const headerWidth = Math.max(0, (window.innerWidth - headerInset * 2) / artboardScale);
        const artboardTop = (viewportHeight - 940 * artboardScale) / 2;
        headerFrame.style.left = `${(1440 - headerWidth) / 2}px`;
        headerFrame.style.width = `${headerWidth}px`;
        headerFrame.style.top = `${(0 - artboardTop) / artboardScale}px`;
        headerFrame.style.height = `${headerHeight / artboardScale}px`;
      }

      if (this.openingPurchase) {
        const progress = clamp(
          (window.scrollY - viewportHeight * OPENING_EXIT_START) /
            (viewportHeight * (OPENING_EXIT_END - OPENING_EXIT_START)),
        );
        const easedProgress = progress * progress * (3 - 2 * progress);
        const opacity = 1 - easedProgress;
        const openingExitDistance = this.reducedMotion.matches ? 0 : OPENING_EXIT_DISTANCE;
        const offset = easedProgress * openingExitDistance;
        this.openingPurchase.style.opacity = String(opacity);
        this.openingPurchase.style.transform = `translate3d(0, ${offset}px, 0)`;
        this.openingPurchase.style.visibility = opacity <= 0.001 ? "hidden" : "visible";
      }

      if (this.transition) {
        const transitionProgress = clamp(
          (window.scrollY - viewportHeight * TRANSITION_REVEAL_START) /
            (viewportHeight * (TRANSITION_REVEAL_END - TRANSITION_REVEAL_START)),
        );
        const easedTransitionProgress =
          transitionProgress * transitionProgress * (3 - 2 * transitionProgress);
        this.transition.style.opacity = String(easedTransitionProgress);
      }

      if (this.header && this.transition && this.realPdp) {
        const scrollPosition = window.scrollY;
        const transitionTop = this.transition.getBoundingClientRect().top + scrollPosition;
        const landerEnd = transitionTop + this.transition.offsetHeight;
        const headerHeight = isMobile ? 64 : Math.min(78, Math.max(58, viewportHeight * 0.072));
        const pdpVisibility = finalPdpBounds && finalPdpBounds.bottom > headerHeight
          ? clamp((headerHeight * 2 - finalPdpBounds.top) / headerHeight)
          : 0;
        let headerPhase = "story";

        if (scrollPosition < landerEnd) headerPhase = "lander";
        if (pdpVisibility > 0) headerPhase = "pdp";

        const opacity = headerPhase === "pdp" ? pdpVisibility : headerPhase === "lander" ? 1 : 0;
        this.header.dataset.phase = headerPhase;
        const offset = headerPhase === "pdp" && !this.reducedMotion.matches
          ? -headerHeight * (1 - pdpVisibility)
          : 0;
        this.header.style.transform = `translate3d(0, ${offset}px, 0)`;
        this.header.style.opacity = String(opacity);
        this.header.style.visibility = opacity <= 0.001 ? "hidden" : "visible";
        this.header.inert = opacity <= 0.001;
      }

      this.root.classList.toggle("mobilePdpActive", mobilePdpAtHeader);
    }

    bindGallery() {
      const gallery = this.root.querySelector("[data-pdp-gallery]");
      const pagination = this.root.querySelector("[data-gallery-pagination]");
      if (!gallery || !pagination) return;

      const items = [...gallery.querySelectorAll("figure")];
      const buttons = [...pagination.querySelectorAll("[data-gallery-index]")];
      const currentIndex = () => {
        const left = gallery.getBoundingClientRect().left;
        return items.reduce((nearest, item, index) =>
          Math.abs(item.getBoundingClientRect().left - left) <
          Math.abs(items[nearest].getBoundingClientRect().left - left) ? index : nearest, 0);
      };
      const update = () => {
        const index = currentIndex();
        const progress = clamp(gallery.scrollLeft / Math.max(gallery.scrollWidth - gallery.clientWidth, 1));
        const fill = items.length > 1 ? (1 + progress * (items.length - 1)) / items.length : 1;
        pagination.style.setProperty("--gallery-progress", String(fill));
        buttons.forEach((button, i) => button.setAttribute("aria-current", String(i === index)));
      };
      const goTo = (index) => {
        if (window.innerWidth > 1023) return;
        const target = items[clamp(index, 0, Math.max(items.length - 1, 0))];
        if (!target) return;
        gallery.scrollTo({
          left: gallery.scrollLeft + target.getBoundingClientRect().left - gallery.getBoundingClientRect().left,
          behavior: this.reducedMotion.matches ? "auto" : "smooth",
        });
      };

      buttons.forEach((button) => this.listen(button, "click", () => goTo(Number(button.dataset.galleryIndex))));
      this.listen(gallery, "keydown", (event) => {
        if (window.innerWidth > 1023) return;
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        goTo(event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : currentIndex() + (event.key === "ArrowRight" ? 1 : -1));
      });
      this.listen(gallery, "scroll", update, { passive: true });
      this.listen(window, "resize", update);
      update();
    }

    optionAt(variant, index) {
      if (Array.isArray(variant.options)) return String(variant.options[index] ?? "");
      return String(variant[`option${index + 1}`] ?? "");
    }

    variantFor(gender, size) {
      if (!gender || !size) return null;
      return this.variants.find(
        (variant) =>
          this.optionAt(variant, this.config.genderOptionIndex) === gender &&
          this.optionAt(variant, this.config.sizeOptionIndex) === size,
      ) || null;
    }

    availableSizes(gender) {
      return new Set(
        this.variants
          .filter(
            (variant) =>
              variant.available &&
              this.optionAt(variant, this.config.genderOptionIndex) === gender,
          )
          .map((variant) => this.optionAt(variant, this.config.sizeOptionIndex)),
      );
    }

    preferredSize(gender) {
      const normalized = gender.toLowerCase();
      const preferred = normalized.includes("female") || normalized.includes("women") ? "38" : "43";
      const available = this.availableSizes(gender);
      if (available.has(preferred)) return preferred;
      return [...available][0] || null;
    }

    bindProductControls() {
      this.controls.forEach((control) => {
        control.querySelectorAll("[data-gender-value]").forEach((button) => {
          this.listen(button, "click", () => {
            this.state.gender = button.dataset.genderValue;
            this.state.size = window.innerWidth < 1024 ? null : this.preferredSize(this.state.gender);
            this.syncProductControls();
          });
        });

        // Shopify option order can change after variants are removed or added.
        const sizeButtons = [...control.querySelectorAll("[data-size-value]")];
        sizeButtons.sort((a, b) => Number(a.dataset.sizeValue) - Number(b.dataset.sizeValue));
        sizeButtons.forEach((button) => {
          button.parentElement.appendChild(button);
          this.listen(button, "click", () => {
            if (button.disabled) return;
            this.state.size = button.dataset.sizeValue;
            this.syncProductControls();
          });
        });

        const form = control.querySelector("form");
        this.listen(form, "submit", (event) => this.addToCart(event, form));
      });

      this.syncProductControls();
    }

    scrollToSizingReference() {
      const target = this.root.querySelector(
        '[data-nami-controls][data-mode="final"] [data-sizing-anchor]',
      );
      if (!target) return;

      const headerOffset = window.innerWidth < 1024 ? 84 : 96;
      const top = Math.max(
        0,
        target.getBoundingClientRect().top + window.scrollY - headerOffset,
      );
      window.scrollTo({
        top,
        behavior: this.reducedMotion.matches ? "auto" : "smooth",
      });
      window.setTimeout(
        () => target.focus({ preventScroll: true }),
        this.reducedMotion.matches ? 0 : 420,
      );
    }

    formatPrice(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: this.config.currency || "USD",
          maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
        }).format(Number(cents) / 100);
      } catch {
        return `$${(Number(cents) / 100).toFixed(2)}`;
      }
    }

    syncProductControls() {
      const variant = this.variantFor(this.state.gender, this.state.size);

      this.controls.forEach((control) => {
        control.querySelectorAll("[data-gender-value]").forEach((button) => {
          const selected = button.dataset.genderValue === this.state.gender;
          button.classList.toggle("selected", selected);
          button.classList.toggle("poleneSelected", selected);
          button.setAttribute("aria-pressed", String(selected));
        });

        control.querySelectorAll("[data-size-value]").forEach((button) => {
          const sizeVariant = this.variantFor(this.state.gender, button.dataset.sizeValue);
          const selected = button.dataset.sizeValue === this.state.size;
          // Missing combinations are hidden; existing sold-out variants stay visible.
          button.hidden = !sizeVariant;
          button.disabled = !sizeVariant?.available;
          button.classList.toggle("selectedSize", selected);
          button.classList.toggle("poleneSelected", selected);
          button.setAttribute("aria-pressed", String(selected));
        });

        const purchaseOptions = control.querySelector("[data-purchase-options]");
        if (purchaseOptions) purchaseOptions.hidden = !this.state.gender;

        const select = control.querySelector(".namiVariantSelect");
        if (select && variant) select.value = String(variant.id);

        const submit = control.querySelector("[data-add-to-bag]");
        const label = control.querySelector("[data-add-label]");
        const isFinalControl = control.dataset.mode === "final";
        const selectionComplete = Boolean(this.state.gender && this.state.size);
        if (submit) {
          submit.disabled = this.state.adding ||
            (selectionComplete ? !variant?.available : !isFinalControl);
          submit.classList.toggle(
            "isSizingShortcut",
            isFinalControl && !selectionComplete,
          );
          if (isFinalControl) {
            const sizingTarget = control.querySelector("[data-sizing-anchor]");
            if (sizingTarget?.id) submit.setAttribute("aria-controls", sizingTarget.id);
          }
        }
        if (label) {
          label.textContent = this.state.adding
            ? "Adding to bag"
            : this.state.size
              ? `Add size ${this.state.size} to bag`
              : this.state.gender
                ? "Select EU size"
                : "Select sizing reference";
        }
      });

      const openingBox = this.root.querySelector("[data-opening-box]");
      if (openingBox && this.state.gender) {
        openingBox.style.setProperty("--purchase-expanded-height", `${Math.max(335, openingBox.scrollHeight)}px`);
      }
      openingBox?.classList.toggle("expanded", Boolean(this.state.gender));

      if (variant) {
        this.root.querySelectorAll("[data-nami-price]").forEach((price) => {
          price.textContent = this.formatPrice(variant.price);
        });
      }
    }

    async addToCart(event, form) {
      event.preventDefault();
      const variant = this.variantFor(this.state.gender, this.state.size);
      if (!variant?.available) {
        if (form.closest('[data-nami-controls][data-mode="final"]')) {
          this.scrollToSizingReference();
        }
        return;
      }
      if (this.state.adding) return;

      this.state.adding = true;
      this.setCommerceError("");
      this.syncProductControls();

      try {
        const response = await fetch(this.config.cartAddUrl || "/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || "Unable to add this size to the bag.");

        if (document.querySelector('#NamiBag[data-ready="true"]')) {
          document.dispatchEvent(new CustomEvent('nami:cart-added', {
            detail: { trigger: event.submitter, keyboard: event.submitter?.matches(':focus-visible') },
          }));
        } else {
          window.location.assign(this.config.cartUrl || '/cart');
        }
      } catch (error) {
        this.setCommerceError(error.message || "Unable to add this size to the bag.");
      } finally {
        this.state.adding = false;
        this.syncProductControls();
      }
    }

    setCommerceError(message) {
      this.root.querySelectorAll("[data-commerce-error]").forEach((element) => {
        element.textContent = message;
        element.hidden = !message;
      });
    }

    bindNewsletter() {
      const form = this.root.querySelector("[data-newsletter-form]");
      const status = form?.querySelector("[data-newsletter-status]");
      if (!form || !status) return;

      this.listen(form, "submit", async (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;

        const button = form.querySelector('button[type="submit"]');
        if (button?.disabled) return;
        if (button) button.disabled = true;
        status.hidden = true;
        status.textContent = "";

        try {
          const response = await fetch(form.action, {
            method: "POST",
            headers: { Accept: "text/html" },
            body: new FormData(form),
            credentials: "same-origin",
          });
          if (!response.ok) throw new Error("Newsletter submission failed");

          form.reset();
          status.setAttribute("role", "status");
          status.textContent = status.dataset.successMessage || "Thank you for joining.";
        } catch {
          status.setAttribute("role", "alert");
          status.textContent = status.dataset.errorMessage || "Please try again.";
        } finally {
          status.hidden = false;
          if (button) button.disabled = false;
        }
      });
    }

    bindSizeGuide() {
      const dialog = this.root.querySelector("[data-size-guide-dialog]");
      if (!dialog) return;

      const tabs = [...dialog.querySelectorAll("[data-size-guide-tab]")];
      const panels = [...dialog.querySelectorAll("[data-size-guide-panel]")];
      const closeDialog = () => {
        if (typeof dialog.close === "function") dialog.close();
        else {
          dialog.removeAttribute("open");
          dialog.removeAttribute("data-fallback-open");
        }
      };
      const activateTab = (selectedTab) => {
        tabs.forEach((tab) => {
          const selected = tab.dataset.sizeGuideTab === selectedTab;
          tab.classList.toggle("activeSizeGuideTab", selected);
          tab.setAttribute("aria-selected", String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.sizeGuidePanel !== selectedTab;
        });
      };

      this.root.querySelectorAll("[data-size-guide-open]").forEach((button) => {
        this.listen(button, "click", () => {
          const selectedGender = this.root.querySelector(
            `[data-gender-value="${CSS.escape(this.state.gender || "")}"]`,
          );
          activateTab(selectedGender?.dataset.genderKey === "women" ? "women" : "men");
          if (typeof dialog.showModal === "function") dialog.showModal();
          else {
            dialog.setAttribute("open", "");
            dialog.setAttribute("data-fallback-open", "");
          }
        });
      });

      tabs.forEach((tab, index) => {
        this.listen(tab, "click", () => activateTab(tab.dataset.sizeGuideTab));
        this.listen(tab, "keydown", (event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
          if (event.key === "Home") nextIndex = 0;
          if (event.key === "End") nextIndex = tabs.length - 1;
          activateTab(tabs[nextIndex].dataset.sizeGuideTab);
          tabs[nextIndex].focus();
        });
      });

      this.listen(dialog.querySelector("[data-size-guide-close]"), "click", closeDialog);
      this.listen(dialog, "click", (event) => {
        if (event.target === dialog) closeDialog();
      });
    }

    bindTabs() {
      this.root.querySelectorAll("[data-tab]").forEach((button) => {
        this.listen(button, "click", () => {
          const selectedTab = button.dataset.tab;
          this.root.querySelectorAll("[data-tab]").forEach((tab) => {
            const selected = tab.dataset.tab === selectedTab;
            tab.classList.toggle("activeTab", selected);
            tab.setAttribute("aria-selected", String(selected));
          });
          this.root.querySelectorAll("[data-tab-panel]").forEach((panel) => {
            panel.hidden = panel.dataset.tabPanel !== selectedTab;
          });
        });
      });
    }

    bindAccordions() {
      this.root.querySelectorAll("[data-accordion-button]").forEach((button) => {
        this.listen(button, "click", () => {
          const panel = button.closest(".poleneAccordion")?.querySelector("[data-accordion-panel]");
          const open = button.getAttribute("aria-expanded") === "true";
          button.setAttribute("aria-expanded", String(!open));
          button.querySelector("span:last-child").textContent = open ? "+" : "−";
          if (panel) panel.hidden = open;
        });
      });
    }

    bindStory() {
      this.storyTrack = this.root.querySelector("[data-story-track]");
      this.storyCanvas = this.root.querySelector("[data-story-canvas]");
      this.storyBeats = [...this.root.querySelectorAll("[data-story-beat]")];
      this.storyContext = this.storyCanvas?.getContext("2d", { alpha: true, desynchronized: true });
      if (!this.storyTrack || !this.storyCanvas || !this.storyContext) return;

      this.storyContext.imageSmoothingEnabled = true;
      this.storyContext.imageSmoothingQuality = "high";
      const requestStory = () => {
        if (this.storyRaf !== null) return;
        this.storyRaf = window.requestAnimationFrame(() => this.updateStory());
      };

      this.listen(window, "scroll", requestStory, { passive: true });
      this.listen(window, "resize", requestStory);
      this.listen(this.reducedMotion, "change", requestStory);
      requestStory();
      this.preloadFrames(requestStory);
    }

    async preloadFrames(onFrame) {
      let nextFrame = 0;
      const worker = async () => {
        while (!this.signal.aborted && nextFrame < this.frameUrls.length) {
          const frame = nextFrame;
          nextFrame += 1;
          try {
            const response = await fetch(this.frameUrls[frame], { cache: "force-cache", signal: this.signal });
            if (!response.ok) throw new Error(`Unable to load story frame ${frame + 1}.`);
            const blob = await response.blob();
            const bitmap = "createImageBitmap" in window ? await createImageBitmap(blob) : await this.imageFromBlob(blob);
            if (this.signal.aborted) {
              bitmap.close?.();
              return;
            }
            this.frameBitmaps[frame] = bitmap;
            onFrame();
          } catch (error) {
            if (!this.signal.aborted) console.warn(error);
          }
        }
      };
      await Promise.all(Array.from({ length: 6 }, worker));
    }

    imageFromBlob(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const image = new Image();
        image.onload = () => {
          URL.revokeObjectURL(url);
          resolve(image);
        };
        image.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Unable to decode story frame."));
        };
        image.src = url;
      });
    }

    closestFrame(target) {
      if (this.frameBitmaps[target]) return target;
      for (let distance = 1; distance < this.frameBitmaps.length; distance += 1) {
        if (target - distance >= 0 && this.frameBitmaps[target - distance]) return target - distance;
        if (target + distance < this.frameBitmaps.length && this.frameBitmaps[target + distance]) return target + distance;
      }
      return -1;
    }

    updateStory() {
      this.storyRaf = null;
      const bounds = this.storyTrack.getBoundingClientRect();
      const distance = Math.max(1, this.storyTrack.offsetHeight - window.innerHeight);
      const progress = clamp(-bounds.top / distance);
      const sequenceProgress = clamp((progress - 0.12) / 0.78);
      const target = Math.round(sequenceProgress * (this.frameBitmaps.length - 1));
      const drawable = this.closestFrame(target);

      if (drawable >= 0 && drawable !== this.lastDrawnFrame) {
        this.storyContext.clearRect(0, 0, 860, 538);
        this.storyContext.drawImage(this.frameBitmaps[drawable], 0, 0, 860, 538);
        this.lastDrawnFrame = drawable;
      }

      this.storyBeats.forEach((beat) => {
        const start = Number(beat.dataset.start);
        const holdStart = Number(beat.dataset.holdStart);
        const holdEnd = Number(beat.dataset.holdEnd);
        const end = Number(beat.dataset.end);
        let opacity = 0;
        if (progress >= start && progress < holdStart) opacity = (progress - start) / (holdStart - start || 0.001);
        else if (progress >= holdStart && progress <= holdEnd) opacity = 1;
        else if (progress > holdEnd && progress <= end) opacity = 1 - (progress - holdEnd) / (end - holdEnd || 0.001);
        beat.style.opacity = String(clamp(opacity));
        beat.setAttribute("aria-hidden", String(opacity <= 0));
      });
    }

    destroy() {
      this.abort.abort();
      if (this.storyRaf !== null) window.cancelAnimationFrame(this.storyRaf);
      if (this.layoutRaf !== null) window.cancelAnimationFrame(this.layoutRaf);
      this.frameBitmaps.forEach((bitmap) => bitmap?.close?.());
      instances.delete(this.root);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll("[data-nami-experience]").forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new NamiProductExperience(root));
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-nami-experience]").forEach((root) => instances.get(root)?.destroy());
  });
})();
